/**
 * Entry Assessment — 10 questions, deterministic Path A/B/C routing.
 *
 * No scoring, no right/wrong — the backend returns a deterministic
 * recommendation per the rules in `services/training/entry_assessment.py`.
 * The learner may retake the assessment at any time.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Compass, RefreshCw } from "lucide-react";


export default function EntryAssessment() {
  const nav = useNavigate();
  const [state, setState] = useState({ intro: null, questions: [], saved: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [decision, setDecision] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/training/assessment");
        setState({
          intro:     r.data?.intro || null,
          questions: r.data?.questions || [],
          saved:     r.data?.saved || null,
        });
        if (r.data?.saved) {
          setAnswers(r.data.saved.answers || {});
          setDecision({
            path:       r.data.saved.path,
            fast_track: r.data.saved.fast_track,
            recommended_lesson_ids: r.data.saved.recommended_lesson_ids,
            reason_tags: r.data.saved.reason_tags,
          });
        }
      } catch (err) {
        setError(extractApiError(
          err,
          "Couldn't load the entry assessment right now.",
        ));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const allAnswered = useMemo(() => {
    if (!state.questions.length) return false;
    return state.questions.every((q) => {
      const v = answers[q.id];
      if (q.kind === "multi_select" || q.kind === "multi_select_max_2") {
        return Array.isArray(v) && v.length > 0;
      }
      return typeof v === "string" && v.length > 0;
    });
  }, [state.questions, answers]);

  async function onSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.post("/training/assessment", { answers });
      setDecision(r.data?.decision || null);
      setState((s) => ({ ...s, saved: r.data?.saved || null }));
    } catch (err) {
      setError(extractApiError(err, "Couldn't save your answers."));
    } finally {
      setSubmitting(false);
    }
  }

  async function onReset() {
    setSubmitting(true);
    try {
      await api.post("/training/assessment/reset", {});
      setAnswers({});
      setDecision(null);
      setState((s) => ({ ...s, saved: null }));
    } catch (err) {
      setError(extractApiError(err, "Couldn't reset the assessment."));
    } finally {
      setSubmitting(false);
    }
  }

  function setSingle(qid, optionId) {
    setAnswers((a) => ({ ...a, [qid]: optionId }));
  }

  function toggleMulti(qid, optionId, exclusive) {
    setAnswers((a) => {
      const current = Array.isArray(a[qid]) ? a[qid] : [];
      let next;
      if (exclusive && optionId === exclusive) {
        next = current.includes(exclusive) ? [] : [exclusive];
      } else {
        const withoutExclusive = exclusive ? current.filter((v) => v !== exclusive) : current;
        if (withoutExclusive.includes(optionId)) {
          next = withoutExclusive.filter((v) => v !== optionId);
        } else {
          next = [...withoutExclusive, optionId];
        }
      }
      return { ...a, [qid]: next };
    });
  }

  return (
    <AppShell>
      <div
        data-testid="entry-assessment-page"
        className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav("/training")}
          data-testid="entry-assessment-back-btn"
          className="mb-4 text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Back to AI Training
        </Button>

        {loading && (
          <p
            data-testid="entry-assessment-loading"
            className="text-sm text-fg-secondary text-center py-12"
          >
            Loading entry assessment&hellip;
          </p>
        )}

        {!loading && error && (
          <Alert
            variant="destructive"
            data-testid="entry-assessment-error"
            className="mb-4"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && state.intro && (
          <>
            <p className="text-xs uppercase tracking-wider text-fg-secondary">
              Entry assessment
            </p>
            <h1
              data-testid="entry-assessment-title"
              className="mt-1 text-3xl sm:text-4xl font-heading font-medium text-fg-primary leading-tight"
            >
              {state.intro.title}
            </h1>
            <p
              data-testid="entry-assessment-intro"
              className="mt-3 text-sm text-fg-secondary leading-relaxed"
            >
              {state.intro.intro}
            </p>

            {decision ? (
              <ResultCard
                decision={decision}
                onReset={onReset}
                busy={submitting}
                onOpenLesson={(id) => nav(`/training/lesson/${id}`)}
              />
            ) : (
              <>
                <div className="mt-8 space-y-6">
                  {state.questions.map((q, idx) => (
                    <QuestionCard
                      key={q.id}
                      idx={idx + 1}
                      q={q}
                      value={answers[q.id]}
                      onSingle={(opt) => setSingle(q.id, opt)}
                      onMulti={(opt) => toggleMulti(q.id, opt, q.exclusive_option)}
                    />
                  ))}
                </div>
                <div className="mt-8 flex items-center gap-3">
                  <Button
                    data-testid="entry-assessment-submit-btn"
                    onClick={onSubmit}
                    disabled={!allAnswered || submitting}
                    className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                  >
                    {submitting ? "Saving\u2026" : "See my starting point"}
                    <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
                  </Button>
                  {!allAnswered && (
                    <p
                      data-testid="entry-assessment-progress-note"
                      className="text-xs text-fg-muted"
                    >
                      Answer every question to continue. No wrong answers.
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}


function QuestionCard({ idx, q, value, onSingle, onMulti }) {
  const isMulti = q.kind === "multi_select" || q.kind === "multi_select_max_2";
  const multiMax = q.max_select || null;
  return (
    <div
      data-testid={`entry-assessment-q-${q.id}`}
      className="rounded-lg border border-border-subtle bg-bg-surface p-5"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-bg-surface-hover text-xs font-medium text-fg-secondary">
          {idx}
        </span>
        <div className="min-w-0 flex-1">
          <p
            data-testid={`entry-assessment-q-${q.id}-text`}
            className="text-sm font-medium text-fg-primary leading-relaxed"
          >
            {q.question}
          </p>
          {q.note && (
            <p className="mt-1 text-xs text-fg-muted leading-relaxed">
              {q.note}
            </p>
          )}
          {isMulti && (
            <p className="mt-1 text-xs text-fg-muted">
              Select {multiMax ? `up to ${multiMax}` : "all that apply"}.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {q.options.map((opt) => {
              const selected = isMulti
                ? Array.isArray(value) && value.includes(opt.id)
                : value === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`entry-assessment-q-${q.id}-opt-${opt.id}`}
                  onClick={() => {
                    if (isMulti) {
                      if (!selected && multiMax && Array.isArray(value) && value.length >= multiMax) {
                        return;
                      }
                      onMulti(opt.id);
                    } else {
                      onSingle(opt.id);
                    }
                  }}
                  className={
                    "rounded-full border px-3 py-1.5 text-xs transition-colors " +
                    (selected
                      ? "border-accent-warm bg-accent-warm/10 text-fg-primary"
                      : "border-border-subtle bg-bg-surface-hover text-fg-secondary hover:text-fg-primary")
                  }
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


function ResultCard({ decision, onReset, onOpenLesson, busy }) {
  const pathLabel = {
    A: "Path A \u00b7 Start Here (modular)",
    B: "Path B \u00b7 Month 1",
    C: "Path C \u00b7 Month 1 (comfort-level)",
  }[decision.path] || `Path ${decision.path}`;
  return (
    <div
      data-testid="entry-assessment-result"
      className="mt-8 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5"
    >
      <div className="flex items-start gap-3">
        <Compass className="h-5 w-5 text-accent-warm flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              data-testid="entry-assessment-result-path"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm uppercase text-[10px] tracking-widest"
            >
              {pathLabel}
            </Badge>
            {decision.fast_track && (
              <Badge
                data-testid="entry-assessment-result-fast-track"
                className="bg-transparent text-fg-primary hover:bg-transparent uppercase text-[10px] tracking-widest border border-accent-warm/60"
              >
                Fast-track suggested
              </Badge>
            )}
          </div>
          <p
            data-testid="entry-assessment-result-copy"
            className="mt-3 text-sm text-fg-primary leading-relaxed"
          >
            We suggest starting with {pathLabel}. People who answered like
            you usually enjoy starting there. You can switch any time &mdash;
            nothing is locked.
          </p>
          <ul
            data-testid="entry-assessment-result-recommended"
            className="mt-4 space-y-2"
          >
            {(decision.recommended_lesson_ids || []).map((id) => (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => onOpenLesson(id)}
                  data-testid={`entry-assessment-result-open-${id}`}
                  className="inline-flex items-center gap-2 text-sm text-fg-primary underline hover:text-accent-warm"
                >
                  Open lesson <code className="font-mono text-xs">{id}</code>
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link to="/training">
              <Button
                data-testid="entry-assessment-result-training-btn"
                size="sm"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                Go to AI Training
                <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
              </Button>
            </Link>
            <Button
              data-testid="entry-assessment-result-reset-btn"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={onReset}
              className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
              Retake the assessment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
